/**
 * Design tokens for the redesigned auth screens (SEE-213 / SEE-212 epic).
 *
 * The new login / registration / passkey / recovery screens are built on the
 * Figma "Web-designs" Material 3 token set, which differs from the app's legacy
 * Poppins/MUI theme. Rather than repeat raw hex/px across ~85 screens we pin the
 * exact Figma values here once and share them.
 *
 * Font: the designs specify "SF Pro". SF Pro is not licensable for the web, so
 * we use the system stack that renders SF Pro on Apple platforms and degrades
 * cleanly elsewhere — matching the existing mobile-auth components.
 */

export const authFontFamily =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro", "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/** Material 3 neutral ramp + brand values used across the auth screens. */
export const authColors = {
  /** Strong text: titles, field labels (Figma neutral-variant/25). */
  textStrong: '#3A3B41',
  /** Placeholder + supporting/helper text (Figma neutral/50). */
  textMuted: '#777779',
  /** Secondary body text e.g. "Remember me". */
  textSecondary: '#5C5C5C',
  /** Default input border (Figma neutral/95). */
  inputBorder: '#F2F0F3',
  /** Slightly stronger neutral border (Figma neutral/90). */
  border: '#E4E2E5',
  /** Checkbox outline (Figma color/neutral/300). */
  checkboxBorder: '#D4D4D4',
  /** Interactive link text (Figma ref/link-blue). */
  link: '#4285F4',
  white: '#FFFFFF',
  error: '#EB5757',
  success: '#079827',
  /** Primary filled action (brand navy, aligns with legacy primary). */
  primary: '#23437F',
  primaryHover: '#1C3159',
  /** M3 disabled surface + label (on-surface @12% / 38%). */
  disabledBg: 'rgba(29, 27, 32, 0.12)',
  disabledText: 'rgba(29, 27, 32, 0.38)',
} as const;

/** Type ramp pulled from the Figma M3 auth tokens (px / lineHeight / weight). */
export const authType = {
  title: { fontSize: 24, lineHeight: '32px', fontWeight: 700, color: authColors.textStrong },
  label: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
    letterSpacing: 0.25,
    color: authColors.textStrong,
  },
  input: { fontSize: 14, lineHeight: '20px', fontWeight: 400, letterSpacing: 0.25 },
  body: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    letterSpacing: 0.25,
    color: authColors.textSecondary,
  },
  helper: { fontSize: 12, lineHeight: '16px', fontWeight: 400 },
  button: { fontSize: 14, lineHeight: '20px', fontWeight: 500, letterSpacing: 0.1 },
} as const;

/** Layout constants shared by the split-panel auth shell. */
export const authLayout = {
  /**
   * Width of the branded splash panel as a share of the viewport. The Figma
   * frame is 1280px with a 536px splash (536/1280 ≈ 41.875%). Expressing it as
   * a ratio — rather than the literal 536px — keeps the design's ~58/42 split
   * at every screen width; a fixed px panel shrinks to a thin strip on wide
   * monitors while the form floats in whitespace.
   */
  splashWidthPct: '41.875%',
  /** Corner radius on the splash panel's inner edge. */
  splashRadius: 32,
  /**
   * Max width of the form content column. Figma's 744px pane − 2×144px padding
   * gives 456px, but on wide monitors that fixed column reads small against the
   * surrounding whitespace, so we widen it modestly (~12%) for presence while
   * keeping the M3 type scale and control heights exactly on spec.
   */
  formMaxWidth: 512,
  /** Breakpoint below which the splash hides and the form goes full width. */
  splashBreakpoint: 'md' as const,
};
